import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/utils/api';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { TrendingUp, Check, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export const Subscription = () => {
  const { subscription, loading, refresh: fetchSubscription, isFreePlan, usage, limits } = useSubscription();
  const [upgrading, setUpgrading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timeout. Please refresh the page.');
      return;
    }

    try {
      const response = await api.get(`/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        fetchSubscription();
        navigate('/dashboard/subscription', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      toast.error('Error checking payment status');
    }
  }, [fetchSubscription, navigate]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/checkout/session', {
        plan: 'basic',
        origin_url: originUrl,
        ...(promoCode.trim() && { promo_code: promoCode.trim() }),
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate upgrade');
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading subscription...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12" data-testid="subscription-page">
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold text-sage-800 mb-2">Subscription</h1>
        <p className="text-gray-600">Manage your clinic subscription plan</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <Card className="bg-white rounded-3xl p-8 shadow-lg border border-stone-100" data-testid="current-plan-card">
          <h2 className="text-2xl font-heading font-bold text-sage-800 mb-6">Current Plan</h2>
          <div className="mb-6">
            <div className="inline-block bg-sage-100 px-6 py-3 rounded-full">
              <span className="text-3xl font-bold capitalize text-sage-700">{subscription?.plan_type || 'Free'}</span>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-700">Owners</span>
                <span className="font-semibold">{usage.owners || 0} / {limits.owners || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-sage-500 rounded-full h-3 transition-all"
                  style={{
                    width: `${Math.min(((usage.owners || 0) / (limits.owners || 1)) * 100, 100)}%`,
                  }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-700">Doctors</span>
                <span className="font-semibold">{usage.doctors || 0} / {limits.doctors || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-terracotta-500 rounded-full h-3 transition-all"
                  style={{
                    width: `${Math.min(((usage.doctors || 0) / (limits.doctors || 1)) * 100, 100)}%`,
                  }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-700">Pet Types</span>
                <span className="font-semibold">{usage.pet_types || 0} / {limits.pet_types > 1000 ? '∞' : limits.pet_types || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-sage-500 rounded-full h-3 transition-all"
                  style={{
                    width: limits.pet_types > 1000 ? '100%' : `${Math.min(((usage.pet_types || 0) / (limits.pet_types || 1)) * 100, 100)}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-sage-500 to-sage-700 rounded-3xl p-8 shadow-2xl text-white relative overflow-hidden" data-testid="upgrade-card">
          {isFreePlan && (
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
          )}
          <h2 className="text-2xl font-heading font-bold mb-4">{isFreePlan ? 'Upgrade to Basic' : 'You\'re on Basic!'}</h2>
          <p className="text-sage-100 mb-6">
            {isFreePlan
              ? 'Unlock more features and grow your clinic with our Basic plan.'
              : 'You have access to all Basic plan features!'}
          </p>
          {isFreePlan ? (
            <>
              <div className="text-4xl font-bold mb-6">$19.99<span className="text-lg text-sage-100">/month</span></div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" /> Up to 200 owners
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" /> 5 pets per owner
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" /> 5 doctors
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" /> Unlimited pet types
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" /> Priority support
                </li>
              </ul>
              <div className="mb-4">
                <div className="flex items-center gap-2 bg-white/10 rounded-2xl px-4 py-2">
                  <Tag className="w-4 h-4 text-sage-100 flex-shrink-0" />
                  <Input
                    placeholder="Promo code (optional)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="bg-transparent border-0 text-white placeholder:text-sage-200 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto text-sm"
                    data-testid="promo-code-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleUpgrade}
                disabled={upgrading}
                className="w-full bg-white text-sage-700 hover:bg-sage-50 rounded-full py-6 text-lg font-semibold"
                data-testid="upgrade-button"
              >
                {upgrading ? 'Processing...' : (
                  <>
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Upgrade Now
                  </>
                )}
              </Button>
            </>
          ) : (
            <div className="text-center py-8">
              <Check className="w-16 h-16 mx-auto mb-4" />
              <p className="text-xl font-semibold">You're all set!</p>
            </div>
          )}
        </Card>
      </div>

      <div className="bg-sage-50 rounded-3xl p-8">
        <h3 className="text-xl font-heading font-bold text-sage-800 mb-4">Plan Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-sage-200">
                <th className="px-4 py-3 text-left text-sage-800">Feature</th>
                <th className="px-4 py-3 text-center text-sage-800">Free</th>
                <th className="px-4 py-3 text-center text-sage-800">Basic</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-sage-100">
                <td className="px-4 py-3 text-gray-700">Owners</td>
                <td className="px-4 py-3 text-center">50</td>
                <td className="px-4 py-3 text-center font-semibold text-sage-700">200</td>
              </tr>
              <tr className="border-b border-sage-100">
                <td className="px-4 py-3 text-gray-700">Pets per Owner</td>
                <td className="px-4 py-3 text-center">2</td>
                <td className="px-4 py-3 text-center font-semibold text-sage-700">5</td>
              </tr>
              <tr className="border-b border-sage-100">
                <td className="px-4 py-3 text-gray-700">Doctors</td>
                <td className="px-4 py-3 text-center">2</td>
                <td className="px-4 py-3 text-center font-semibold text-sage-700">5</td>
              </tr>
              <tr className="border-b border-sage-100">
                <td className="px-4 py-3 text-gray-700">Pet Types</td>
                <td className="px-4 py-3 text-center">5</td>
                <td className="px-4 py-3 text-center font-semibold text-sage-700">Unlimited</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700">Support</td>
                <td className="px-4 py-3 text-center">Standard</td>
                <td className="px-4 py-3 text-center font-semibold text-sage-700">Priority</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
